import { ROLE } from '../static';

/** Values the API sends in `pendingProfileQuestions`. */
export const PROFILE_QUESTION = Object.freeze({
  FRL: 'frl',
  PARENT_EDUCATION: 'parentEducation',
});

const KNOWN_QUESTIONS = Object.values(PROFILE_QUESTION);

export const PROFILE_QUESTIONS_SAVE_ERROR = 'We could not save your answers. Please try again.';

const toOptions = values => values.map(value => ({ label: value, value }));

export const FRL_QUESTION_LABEL = 'Do you qualify for Free or Reduced lunch at your school?';

export const FRL_OPTIONS = toOptions(['Yes', 'No', "I don't know", 'Not applicable']);

export const PARENT_EDUCATION_QUESTION_LABEL =
  'To the best of your knowledge, what is the highest level of education that any of your parent(s)/guardian(s) have received?';

export const PARENT_EDUCATION_OPTIONS = toOptions([
  'Did not finish high school',
  'Received a General Equivalency Diploma (GED) or High School Equivalency Diploma (HSE)',
  'High school diploma',
  'Went to college but did not graduate',
  'Graduated from college',
  "I don't know",
]);

/**
 * Whether the mentee's partner asks the FRL questions (onboarding question and pop-up). The backend computes this from the
 * partner setting, so the client never reads the setting itself.
 *
 * @param {{ role?: string, frlQuestionsEnabled?: unknown } | null | undefined} user
 * @returns {boolean}
 */
export const isFrlQuestionsEnabled = user => user?.role === ROLE.MENTEE && user?.frlQuestionsEnabled === true;

/**
 * Onboarding form sections without the FRL row, for mentees whose partner does not ask it.
 *
 * @param {Array<{ childrens?: Array<{ childrens?: Array<{ key?: string }> }> }>} sections
 * @returns {typeof sections}
 */
export const withoutFrlQuestion = sections =>
  sections.map(section => ({
    ...section,
    childrens: (section.childrens || []).filter(
      row => !(row.childrens || []).some(field => field.key === PROFILE_QUESTION.FRL)
    ),
  }));

/**
 * The questions a logged-in mentee still has to answer, in display order.
 * Unknown values are dropped so an older client never renders a question it cannot handle.
 *
 * @param {{ role?: string, pendingProfileQuestions?: unknown } | null | undefined} user
 * @returns {string[]}
 */
export const getPendingProfileQuestions = user => {
  if (user?.role !== ROLE.MENTEE || !Array.isArray(user?.pendingProfileQuestions)) {
    return [];
  }
  return KNOWN_QUESTIONS.filter(question => user.pendingProfileQuestions.includes(question));
};

/**
 * Request body for `POST mentee/profileQuestions`: only the pending questions, trimmed.
 *
 * @param {string[]} pending
 * @param {Record<string, unknown>} values
 * @returns {Record<string, string>}
 */
export const buildProfileQuestionsPayload = (pending, values) =>
  pending.reduce((payload, question) => {
    const value = values?.[question];
    if (typeof value === 'string' && value.trim()) {
      payload[question] = value.trim();
    }
    return payload;
  }, {});
